import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, Globe, Copy, Check, Plus, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Spinner from '../components/ui/Spinner';
import Button from '../components/ui/Button';
import { getMe } from '../services/authService';
import * as authService from '../services/authService';
import { getIPWhitelist, updateIPWhitelist } from '../services/settingsService';

export default function SecuritySettingsPage() {
  const queryClient = useQueryClient();
  const { data: me, isLoading } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: getMe,
  });

  const [setupStep, setSetupStep] = useState<'idle' | 'qr' | 'backup'>('idle');
  const [setupData, setSetupData] = useState<authService.TotpSetupData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disableMode, setDisableMode] = useState(false);
  const [loading, setLoading] = useState(false);

  if (isLoading) return <div className="flex justify-center py-16"><Spinner className="size-8" /></div>;

  const totpEnabled = me?.totpEnabled === true;
  const totpEnabledAt = me?.totpEnabledAt as unknown as string | null;

  const handleSetup = async () => {
    setLoading(true);
    try {
      const data = await authService.totpSetup();
      setSetupData(data);
      setSetupStep('qr');
    } catch {
      toast.error('2FA 설정을 시작할 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (code: string) => {
    setLoading(true);
    try {
      const result = await authService.totpVerifySetup(code);
      setBackupCodes(result.backupCodes);
      setSetupStep('backup');
      queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
      toast.success('2FA가 활성화되었습니다.');
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '인증 코드가 올바르지 않습니다.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (code: string) => {
    setLoading(true);
    try {
      await authService.totpDisable(code);
      setDisableMode(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
      toast.success('2FA가 비활성화되었습니다.');
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '인증 코드가 올바르지 않습니다.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900">보안 설정</h1>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <Shield className="size-5 text-gray-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">2단계 인증 (TOTP)</h3>
            <p className="text-sm text-gray-500">인증 앱을 사용한 추가 보안</p>
          </div>
        </div>

        {totpEnabled ? (
          /* ── 활성화 상태 ── */
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block size-2 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-green-700">2단계 인증이 활성화되어 있습니다</span>
            </div>
            {totpEnabledAt && (
              <p className="text-sm text-gray-500 mb-4">설정일: {new Date(totpEnabledAt).toLocaleDateString('ko-KR')}</p>
            )}

            {disableMode ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">비활성화하려면 현재 인증 코드를 입력하세요.</p>
                <TotpCodeInput onComplete={handleDisable} disabled={loading} />
                <button onClick={() => setDisableMode(false)} className="text-sm text-gray-500 hover:text-gray-700">취소</button>
              </div>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setDisableMode(true)}>
                2FA 비활성화
              </Button>
            )}
          </div>
        ) : setupStep === 'idle' ? (
          /* ── 비활성화 상태 ── */
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block size-2 rounded-full bg-gray-300" />
              <span className="text-sm text-gray-600">2단계 인증이 비활성화되어 있습니다</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">인증 앱(Google Authenticator, Authy 등)을 사용하여 보안을 강화하세요.</p>
            <Button size="sm" onClick={handleSetup} loading={loading}>
              2FA 설정하기
            </Button>
          </div>
        ) : setupStep === 'qr' && setupData ? (
          /* ── QR 코드 + 검증 ── */
          <div className="space-y-4">
            <p className="text-sm text-gray-600">인증 앱에서 아래 QR 코드를 스캔하세요.</p>
            <div className="flex justify-center">
              <img src={setupData.qrCodeDataUrl} alt="TOTP QR Code" className="w-48 h-48" />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">수동 입력 키:</p>
              <SecretDisplay secret={setupData.secret} />
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">인증 앱의 6자리 코드를 입력하세요.</p>
              <TotpCodeInput onComplete={handleVerifySetup} disabled={loading} />
            </div>
            <button onClick={() => setSetupStep('idle')} className="text-sm text-gray-500 hover:text-gray-700">취소</button>
          </div>
        ) : setupStep === 'backup' ? (
          /* ── 백업 코드 ── */
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 font-medium">아래 백업 코드를 안전한 곳에 저장하세요.</p>
              <p className="text-sm text-amber-700 mt-1">이 코드는 다시 볼 수 없습니다.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, i) => (
                <div key={i} className="font-mono text-sm bg-gray-50 p-2 rounded text-center text-gray-800">
                  {code}
                </div>
              ))}
            </div>
            <CopyButton text={backupCodes.join('\n')} />
            <Button size="sm" onClick={() => setSetupStep('idle')} className="w-full">
              완료
            </Button>
          </div>
        ) : null}
      </div>

      {/* IP Whitelist Section */}
      <IPWhitelistSection />
    </div>
  );
}

// ── IP Whitelist Section ──

const IP_REGEX = /^(?:(?:\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?|[0-9a-fA-F:]+)$/;

function IPWhitelistSection() {
  const queryClient = useQueryClient();
  const { data: config, isLoading } = useQuery({
    queryKey: ['admin', 'ip-whitelist'],
    queryFn: getIPWhitelist,
  });

  const [enabled, setEnabled] = useState(false);
  const [allowedIPs, setAllowedIPs] = useState<string[]>([]);
  const [excludeLogin, setExcludeLogin] = useState(true);
  const [newIP, setNewIP] = useState('');
  const [ipError, setIpError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
      setAllowedIPs(config.allowedIPs);
      setExcludeLogin(config.excludeLogin);
      setDirty(false);
    }
  }, [config]);

  const currentIP = config?.currentIP ?? '';
  const currentIPInList = allowedIPs.length > 0 && currentIP ? allowedIPs.some((ip) => ip === currentIP || (ip.includes('/') && currentIP.startsWith(ip.split('/')[0].split('.').slice(0, 3).join('.')))) : false;

  const addIP = () => {
    const trimmed = newIP.trim();
    if (!trimmed) return;
    if (!IP_REGEX.test(trimmed)) {
      setIpError('유효하지 않은 IP 형식입니다.');
      return;
    }
    if (allowedIPs.includes(trimmed)) {
      setIpError('이미 추가된 IP입니다.');
      return;
    }
    setAllowedIPs([...allowedIPs, trimmed]);
    setNewIP('');
    setIpError('');
    setDirty(true);
  };

  const removeIP = (ip: string) => {
    setAllowedIPs(allowedIPs.filter((i) => i !== ip));
    setDirty(true);
  };

  const handleToggle = () => {
    if (!enabled && allowedIPs.length === 0) {
      toast.error('최소 1개 이상의 IP를 추가해야 활성화할 수 있습니다.');
      return;
    }
    if (!enabled) {
      setConfirmEnable(true);
      return;
    }
    setEnabled(false);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateIPWhitelist({ enabled, allowedIPs, excludeLogin });
      queryClient.invalidateQueries({ queryKey: ['admin', 'ip-whitelist'] });
      setDirty(false);
      if (result.message) {
        toast(result.message, { icon: '⚠️', duration: 5000 });
      } else {
        toast.success('IP 화이트리스트 설정이 저장되었습니다.');
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '저장에 실패했습니다.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <div className="bg-white border border-gray-200 rounded-xl p-6"><Spinner className="size-5 mx-auto" /></div>;

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="size-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <Globe className="size-5 text-gray-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">IP 화이트리스트</h3>
            <p className="text-sm text-gray-500">허용된 IP에서만 Admin 접근 가능</p>
          </div>
          {/* Toggle switch */}
          <button onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-gray-900' : 'bg-gray-200'}`}>
            <span className={`inline-block size-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Warning: current IP not in list */}
        {enabled && allowedIPs.length > 0 && !currentIPInList && (
          <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 text-yellow-700 p-3 rounded-lg mb-4 text-sm">
            <AlertTriangle className="size-4 mt-0.5 shrink-0" />
            <span>현재 접속 IP가 허용 목록에 포함되어 있지 않습니다. 활성화하면 접근이 차단될 수 있습니다.</span>
          </div>
        )}

        {/* IP list */}
        <div className="space-y-2 mb-4">
          <p className="text-sm font-medium text-gray-600">허용 IP 목록</p>
          {allowedIPs.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">추가된 IP가 없습니다.</p>
          ) : (
            <div className="space-y-1">
              {allowedIPs.map((ip) => (
                <div key={ip} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-sm font-mono text-gray-800">{ip}</span>
                  <button onClick={() => removeIP(ip)} className="text-red-400 hover:text-red-600">
                    <X className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add IP input */}
          <div className="flex gap-2">
            <input type="text" value={newIP} onChange={(e) => { setNewIP(e.target.value); setIpError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && addIP()}
              placeholder="예: 192.168.1.0/24"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg outline-none focus:ring-1 focus:ring-primary-500 font-mono ${ipError ? 'border-red-400' : 'border-gray-300'}`} />
            <button onClick={addIP}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              <Plus className="size-4" /> 추가
            </button>
          </div>
          {ipError && <p className="text-sm text-red-500">{ipError}</p>}
        </div>

        {/* Exclude login checkbox */}
        <label className="flex items-start gap-2 mb-4">
          <input type="checkbox" checked={excludeLogin}
            onChange={(e) => { setExcludeLogin(e.target.checked); setDirty(true); }}
            className="mt-0.5 rounded border-gray-300" />
          <div>
            <span className="text-sm text-gray-700">로그인 페이지 IP 체크 제외</span>
            <p className="text-sm text-gray-500">(잠금 방지: 로그인은 모든 IP에서 허용)</p>
          </div>
        </label>

        {/* Current IP */}
        {currentIP && (
          <p className="text-sm text-gray-500 mb-4">현재 접속 IP: <span className="font-mono">{currentIP}</span></p>
        )}

        {/* Save button */}
        <Button size="sm" onClick={handleSave} loading={saving} disabled={!dirty}>
          저장
        </Button>
      </div>

      {/* Enable confirmation dialog */}
      {confirmEnable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">IP 화이트리스트 활성화</h3>
            <p className="text-sm text-gray-600 mb-4">
              IP 화이트리스트를 활성화하면 목록에 없는 IP에서는 Admin에 접근할 수 없습니다. 계속하시겠습니까?
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmEnable(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                취소
              </button>
              <button onClick={() => { setEnabled(true); setDirty(true); setConfirmEnable(false); }}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800">
                활성화
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TotpCodeInput({ onComplete, disabled }: { onComplete: (code: string) => void; disabled: boolean }) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (idx: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[idx] = value;
    setDigits(next);
    if (value && idx < 5) inputRefs.current[idx + 1]?.focus();
    const code = next.join('');
    if (code.length === 6) onComplete(code);
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < pasted.length && i < 6; i++) next[i] = pasted[i];
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) onComplete(pasted);
  };

  return (
    <div className="flex justify-center gap-2" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input key={i} ref={(el) => { inputRefs.current[i] = el; }}
          type="text" inputMode="numeric" maxLength={1} value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled} autoFocus={i === 0}
          className="w-12 h-14 text-center text-xl font-mono border-2 border-gray-300 rounded-lg
            focus:border-gray-900 focus:outline-none transition-colors disabled:opacity-50" />
      ))}
    </div>
  );
}

function SecretDisplay({ secret }: { secret: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
      <code className="text-sm font-mono text-gray-800 flex-1 select-all">{secret}</code>
      <button onClick={copy} className="p-1 text-gray-500 hover:text-gray-700">
        {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
      </button>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy}
      className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 mx-auto">
      {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
      {copied ? '복사됨' : '코드 복사'}
    </button>
  );
}
